import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface CartItem {
  id: string;
  name: string;
  model: string;
  size: string;
  price: number;
  originalPrice: number;
  quantity: number;
  image: string;
  isCustom: boolean;
  customDesign?: {
    image?: string;
    prompt?: string;
    baseColor?: string;
  };
  isBundle?: boolean;
  bundleQuantity?: number;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  setOpen: (open: boolean) => void;
  
  getSubtotal: () => number;
  getShipping: () => number;
  getTotal: () => number;
  getTotalItems: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (item) => {
        const { items } = get();
        const existingItem = items.find(
          i => i.id === item.id && i.size === item.size && !item.isCustom
        );
        
        if (existingItem && !item.isCustom) {
          set({
            items: items.map(i =>
              i.id === existingItem.id && i.size === item.size
                ? { ...i, quantity: i.quantity + 1 }
                : i
            )
          });
        } else {
          set({ items: [...items, { ...item, quantity: 1 }] });
        }
        set({ isOpen: true });
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        
        set({
          items: get().items.map(item =>
            item.id === id ? { ...item, quantity } : item
          )
        });
      },

      removeItem: (id) => {
        set({
          items: get().items.filter(item => item.id !== id)
        });
      },

      clearCart: () => {
        set({ items: [] });
      },

      setOpen: (isOpen) => set({ isOpen }),

      getSubtotal: () => {
        return get().items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      },

      getShipping: () => {
        const subtotal = get().getSubtotal();
        const hasBundle = get().items.some(item => item.isBundle);
        if (hasBundle || subtotal >= 50) return 0;
        return 4.99;
      },

      getTotal: () => {
        return get().getSubtotal() + get().getShipping();
      },

      getTotalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      }
    }),
    {
      name: 'funzy-feets-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);
